import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormBuilder, Validators, AbstractControl, ValidatorFn, FormControl } from '@angular/forms';
import { FormGroup } from '@angular/forms';
import { ReferenceArea } from '../../models/reference-area';
import { Subscription, Observable } from 'rxjs';
import { UserProfileService } from '../../../users/services/user-profile.service';
import { environment } from '../../../../../environments/environment';
import { ProjectDataService } from '../../../project/services/project-data.service';
import { Project } from '../../../project/models/project';
import { map } from 'rxjs/operators';
import { FormValidationService } from '../../../../shared/services/form-validation.service';

@Component({
  selector: 'app-reference-area-edit-form',
  template: require('./reference-area-edit-form.component.html'),
  styles: [require('./reference-area-edit-form.component.sass')]
})
export class ReferenceAreaEditFormComponent implements OnInit, OnDestroy {

  @Input() referenceArea: ReferenceArea;
  @Input() projectId: number;
  @Input() editEnabled: boolean = true;
  @Input() usedReferenceIds: number[] | null;
  @Input() isSingleTrain: boolean = false;

  @Output() save = new EventEmitter<ReferenceArea>();
  @Output() delete = new EventEmitter<ReferenceArea>();
  @Output() cancel = new EventEmitter();
  
  isNewReferenceArea = true;
  referenceAreaForm: FormGroup;
  types: string[] = environment.config.referenceAreas.types;
  projects$: Observable<Project[]>;

  get project() { return this.referenceAreaForm.get('projectId'); }
  get name() { return this.referenceAreaForm.get('name'); }
  get description() { return this.referenceAreaForm.get('description'); }
  get abbreviation() { return this.referenceAreaForm.get('abbreviation'); }
  get type() { return this.referenceAreaForm.get('type'); }
  get latLongList() { return this.referenceAreaForm.get('latLongList'); }

  constructor(
    private fb: FormBuilder,
    public userProfileService: UserProfileService,
    private projectService: ProjectDataService,
    public formValidation: FormValidationService

) {
      this.referenceAreaForm = this.fb.group({
        projectId: ['', Validators.required],
        name: ['', Validators.required],
        description: ['', Validators.required],
        abbreviation: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(5)]],
        type: ['', Validators.required],
        latLongList: ['', [Validators.required, areaValidator(3)]]
      });
    }

  ngOnInit(): void {
    this.projects$ = this.projectService.getAllProjects().pipe(
      map(projects => projects.filter(project => project.id > 0))
    );

    this.isNewReferenceArea = !(this.referenceArea && this.referenceArea.id);
    this.referenceAreaForm.patchValue(this.referenceArea ? this.referenceArea : {
      projectId: this.projectId > 0 ? this.projectId : null
    });
    
    if (this.editEnabled) {
      this.referenceAreaForm.enable();
    } else {
      this.referenceAreaForm.disable();
    }

    if (!this.isNewReferenceArea) {
      this.referenceAreaForm.get('type')?.disable({onlySelf: true});
    }

    if (this.projectId > 0 || (this.referenceArea && this.referenceArea.projectId > 0)) {
      this.referenceAreaForm.get('projectId')?.disable({onlySelf: true});
    }
  }

  enableForm() {
    this.editEnabled = true;
    this.referenceAreaForm.enable();
    if (!this.isNewReferenceArea) {
      this.referenceAreaForm.get('type')?.disable({onlySelf: true});
    }
    if (this.projectId > 0 || (this.referenceArea && this.referenceArea.projectId > 0)) {
      this.referenceAreaForm.get('projectId')?.disable({onlySelf: true});
    }
  }

  onSave = () => {
    if(this.referenceAreaForm.valid){
      const positions = this.referenceAreaForm.value.latLongList.split(" ");
      const referenceArea: ReferenceArea = {
        id: this.isNewReferenceArea ? undefined : this.referenceArea.id,
        name: this.referenceAreaForm.value.name,
        description: this.referenceAreaForm.value.description,
        abbreviation: this.referenceAreaForm.value.abbreviation,
        type: this.isNewReferenceArea ? this.referenceAreaForm.value.type : this.referenceArea.type,
        area: {
          positions: positions.map(position => {
            const latLong = position.split(",");
            return {
              latitude: latLong[0],
              longitude: latLong[1]
            }
          })
        },
        projectId: this.isNewReferenceArea ?  (this.projectId > 0 ? this.projectId : this.referenceAreaForm.value.projectId) : this.referenceArea.projectId
      }
      this.save.emit(referenceArea)
    }
      else{
        this.formValidation.validateAllFormFields(this.referenceAreaForm);
      }
    }

  onCancel = () => {
    this.cancel.emit();
  }

  onDelete = () => {
    this.delete.emit(this.referenceArea);
  }

  uniqueReferenceIdValidator = (control: FormControl) => {
    if (!control.value) {
      return null;
    } else {
      return this.usedReferenceIds && !!this.usedReferenceIds.find(id => id === control.value) ? {
        duplicateId: true
      } : null
    }
  }

  ngOnDestroy(): void {
    this.referenceAreaForm.reset();
  }
}
  /**
 * Validate an area is entered in the correct format.
 * A minimum of 3 geo-positions are required.
 * A geo-position will be a string: latitude,longitude
 * geo-positions should contain a single space between
 * Example:
 * 48.858093,2.294694 48.857000,2.297000 48.857000,2.291000
 */
export function areaValidator(positionLength: number): ValidatorFn {
  return (control: AbstractControl): {[key: string]: any} | null => {
    const area: string = control.value;
    const positions = area && area.split(" ");

    if (positions) {
      if (positions.length < positionLength) {
        return {
          minPositions: positionLength,
          actualPositions: positions.length
        };
      } 
  
      for (let position of positions) {
        const positionSplit = position.split(",");
  
        if (positionSplit.length != 2) {
          return {cannotParsePosition: position};
        } else {
  
          if (positionSplit[0] === "") {
            return {latitudeMissing: position};
          }
          if (positionSplit[1] === "") {
            return {longitudeMissing: position};
          }
  
          const latitude = parseInt(positionSplit[0]);
          const longitude = parseInt(positionSplit[1]);
  
          if (isNaN(latitude) || (latitude < -90 || latitude > 90)) {
            const latitudeVal = isNaN(latitude) ? positionSplit[0] : latitude;
            return {latitudeInvalid: latitudeVal};
          }
  
          if (isNaN(longitude) || (longitude < -180 || longitude > 180)) {
            const longitudeVal = isNaN(longitude) ? positionSplit[1] : longitude;
            return {longitudeInvalid: longitudeVal};
          }
        }
      }
      return null;
    } else {
      return null
    }
  }
}
